import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  InputBase,
  Avatar,
  Popover,
  useMediaQuery,
  useTheme,
  Button,
  Badge,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  NotificationsNone as NotificationsIcon,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useAuth, useCurrentTalentProfile } from "../hooks";
import TalentandUserToggleMenu from "../components/Dashboard/Talent/TalentCustomMenu_updated_no";
import { useLocation, useNavigate } from "react-router-dom";
import FCMNotifications from "./FCMNotifications_new";
import SocketNotification from "./SocketNotification";
import { Link } from "react-router-dom";
import useCurrentGTP from "../hooks/useGTP";
import {
  useAllNotificationRead,
  useGetNotification,
} from "../services/api/notification.api";
import { NotificationFullData } from "./UserNotification";
import { SocketNotificationData } from "../components/socketNotification/types";
import useThemeStore from "../services/store/useThemeStore";
import { useNotificationSound } from "../hooks/useNotificationSound";
import { useMessageDataStore } from "../services/store/UseMessageData";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../components/common/LanguageSelector";
import { AIRBANK_DARK_CANVAS } from "../constants/airbankSurfaces";

const airbankOrange = "#f9a825";
const airbankOrangeDeep = "#e69100";

interface HeaderProps {
  toggleDrawer: () => void;
  onClickCreate?: () => void;
  coffeeCallNotificationfromLayout?: any;
  regularCallNotificationfromLayout?: any;
  groupCallInvitationfromLayout?: any;
  callConfirmationfromLayout?: any;
  groupCallNotificationfromLayout?: any;
  callRemainderfromLayout?: any;
  setBadgeVisible: (visible: boolean) => void;
  badgeVisible: boolean;
  socketNotification: any[];
}

const Header: React.FC<HeaderProps> = ({
  toggleDrawer,
  coffeeCallNotificationfromLayout,
  regularCallNotificationfromLayout,
  groupCallInvitationfromLayout,
  callConfirmationfromLayout,
  groupCallNotificationfromLayout,
  callRemainderfromLayout,
  socketNotification,
  setBadgeVisible,
  badgeVisible,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { setTaanaUnreadMessageCounter } = useMessageDataStore();
  const location = useLocation();
  const currrentPath = location.pathname;
  const taanaPage = currrentPath.includes("taana/");
  const { t } = useTranslation();

  const { userInfo } = useAuth();
  const { currentGTP } = useCurrentGTP();
  const { currentProfile } = useCurrentTalentProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { playSound } = useNotificationSound();
  const [joinRegularChannel, setJoinRegularChannel] = useState<{
    channelName: string | null;
    cardId: string | null;
    bookingId: string | null;
    suit: string | null;
  } | null>(null);
  const [joinGroupChannel, setJoinGroupChannel] = useState<{
    channelName: string | null;
    cardId: string | null;
    bookingId: string | null;
    suit: string | null;
  } | null>(null);

  const { data: notificationData, refetch } = useGetNotification({
    key: ["notification", currentProfile?.id, currentGTP?.id],
    params: {
      offset: 0,
      limit: 10,
      ...(currentGTP?.id && { gtp_id: currentGTP?.id }),
      ...(currentProfile?.id && { talent_id: currentProfile?.id }),
    },
    options: {
      refetchInterval: 30000,
    },
  });

  const { update: updateAllNotificationRead } = useAllNotificationRead({
    key: ["notificationRead"],
  });

  const notificationList = useMemo(() => {
    const raw = notificationData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [notificationData?.data]);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const studioRoute = currentProfile ? "/airbank-studio" : "/airbank-studio";
  const isStudioRouteActive = location.pathname === studioRoute;

  // notifications popover
  const [anchorPopover, setAnchorPopover] = React.useState(null);
  const openPopover = Boolean(anchorPopover);

  useEffect(() => {
    if (socketNotification.length > 0) {
      socketNotification.forEach((notification: SocketNotificationData) => {
        if (
          notification.notification_type === "booking-started" &&
          !notification.is_group_session
        ) {
          setJoinRegularChannel({
            channelName: notification?.channel_name || null,
            cardId: notification?.card_id || null,
            bookingId: notification?.booking_id || null,
            suit: notification?.suit || null,
          });
        } else if (
          notification.notification_type === "booking-started" &&
          notification.is_group_session
        ) {
          setJoinGroupChannel({
            channelName: notification?.channel_name || null,
            cardId: notification?.card_id || null,
            bookingId: notification?.booking_id || null,
            suit: notification?.suit || null,
          });
        } else if (
          notification.notification_type === "booking-ended" &&
          !notification.is_group_session
        ) {
          setJoinRegularChannel(null);
        } else if (
          notification.notification_type === "booking-ended" &&
          notification.is_group_session
        ) {
          setJoinGroupChannel(null);
        } else {
          setJoinGroupChannel(null);
          setJoinRegularChannel(null);
        }
      });
      refetch();
    }
  }, [socketNotification]);

  useEffect(() => {
    if (notificationList.length > 0) {
      const isUnreadNotification = notificationList.some(
        (notification) => !notification.is_read
      );
      setBadgeVisible(isUnreadNotification);
    }
  }, [notificationList, setBadgeVisible]);

  const handlePopoverclick = (event: any) => {
    setAnchorPopover(event.currentTarget);
    if (badgeVisible) {
      updateAllNotificationRead(
        {
          ...(currentProfile?.id && { talent_id: currentProfile?.id }),
          ...(currentGTP?.id && { gtp_id: currentGTP?.id }),
        },
        {
          onSuccess: () => {
            setBadgeVisible(false); // User viewed the popover and all notifications are marked as read
          },
          onError: (error) => {
            console.error("Failed to mark all notifications as read:", error);
          },
        }
      );
    }
  };

  const handlePopoverClose = () => {
    setAnchorPopover(null);
  };

  // Determine dark mode
  const { darkMode: isDarkMode } = useThemeStore();

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: isDarkMode ? AIRBANK_DARK_CANVAS : "rgba(255, 255, 255, 0.98)",
          borderBottom: isDarkMode
            ? "1px solid rgba(249, 168, 37, 0.14)"
            : "1px solid rgba(230, 145, 0, 0.18)",
          color: isDarkMode ? "#E5E7EB" : "#111827",
          boxShadow: isDarkMode
            ? "none"
            : "0 6px 20px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 1, sm: 2.5, md: 4 },
            minHeight: { xs: 56, sm: 72 },
            gap: { xs: 0.5, sm: 1.5 },
          }}
        >
          {/* Left */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <Tooltip title="Toggle sidebar" arrow>
              <IconButton
                onClick={toggleDrawer}
                sx={{
                  color: isDarkMode ? "#E5E7EB" : "#111827",
                  padding: { xs: 0.75, sm: 1 },
                  borderRadius: 2,
                  border: `1px solid ${
                    isDarkMode
                      ? "rgba(249, 168, 37, 0.24)"
                      : "rgba(230, 145, 0, 0.18)"
                  }`,
                  backgroundColor: isDarkMode
                    ? "rgba(249, 168, 37, 0.06)"
                    : "rgba(249, 168, 37, 0.04)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    backgroundColor: isDarkMode
                      ? "rgba(249, 168, 37, 0.14)"
                      : "rgba(249, 168, 37, 0.1)",
                    transform: "translateY(-1px)",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: 20, sm: 24 },
                  },
                }}
              >
                <MenuIcon
                  sx={{ color: isDarkMode ? "#fde68a" : airbankOrangeDeep }}
                />
              </IconButton>
            </Tooltip>
            {!isMobile && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: 20,
                  fontFamily: '"Lexend", sans-serif',
                  letterSpacing: "0.04em",
                  background:
                    "linear-gradient(90deg, #f59e0b 0%, #e69100 45%, #fbbf24 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                AIRBANK
              </Typography>
            )}
          </Box>

          {/* Search (hide on xs) */}
          {!isMobile && (
            <Box
              sx={{
                position: "relative",
                flex: 1,
                maxWidth: 420,
                mx: { xs: 1, md: 4 },
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(249, 250, 251, 0.85)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                px: 2,
                height: 44,
                border: `1px solid ${
                  isDarkMode
                    ? "rgba(249, 168, 37, 0.18)"
                    : "rgba(230, 145, 0, 0.16)"
                }`,
                boxShadow: isDarkMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.03)"
                  : "inset 0 1px 0 rgba(255,255,255,0.85)",
              }}
            >
              <SearchIcon
                sx={{ color: isDarkMode ? "#9CA3AF" : "#9CA3AF", mr: 1 }}
              />
              <InputBase
                placeholder="Search Airbank..."
                sx={{
                  flex: 1,
                  fontFamily: '"Lexend", sans-serif',
                  fontSize: 14,
                  color: isDarkMode ? "#E5E7EB" : "#111827",
                }}
                inputProps={{ "aria-label": "search" }}
              />
            </Box>
          )}

          {/* Right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1, md: 2 },
            }}
          >
            <Tooltip title="Open Studio" arrow>
              <IconButton
                onClick={() => navigate(studioRoute)}
                aria-label="open studio"
                sx={{
                  backgroundColor: isStudioRouteActive
                    ? isDarkMode
                      ? "rgba(249, 168, 37, 0.18)"
                      : "rgba(249, 168, 37, 0.12)"
                    : "transparent",
                  border: `1.5px solid ${
                    isStudioRouteActive
                      ? airbankOrange
                      : isDarkMode
                        ? "rgba(148, 163, 184, 0.4)"
                        : "rgba(148, 163, 184, 0.6)"
                  }`,
                  borderRadius: 1.5,
                  padding: { xs: 0.75, sm: 1 },
                  color: isStudioRouteActive
                    ? airbankOrangeDeep
                    : isDarkMode
                      ? "var(--neutralWhite)"
                      : "var(--neutral-500)",
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    backgroundColor: isDarkMode
                      ? "rgba(249, 168, 37, 0.22)"
                      : "rgba(249, 168, 37, 0.12)",
                    transform: isMobile ? "scale(1.05)" : "none",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: 18, sm: 20 },
                  },
                }}
              >
                <SpaceDashboardOutlined />
              </IconButton>
            </Tooltip>


            <>
              <Tooltip title="Notifications" arrow>
                <IconButton
                  aria-describedby={"notifications-popover"}
                  onClick={handlePopoverclick}
                  sx={{
                    position: "relative",
                    padding: { xs: 0.75, sm: 1 },
                    borderRadius: { xs: 1.5, sm: 1.5 },
                    backgroundColor: badgeVisible
                      ? isDarkMode
                        ? "rgba(239, 68, 68, 0.15)"
                        : "rgba(239, 68, 68, 0.1)"
                      : "transparent",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: badgeVisible
                        ? isDarkMode
                          ? "rgba(239, 68, 68, 0.25)"
                          : "rgba(239, 68, 68, 0.2)"
                        : isDarkMode
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.05)",
                      transform: "scale(1.05)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: { xs: 20, sm: 24 },
                    },
                  }}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={!badgeVisible}
                    sx={{
                      "& .MuiBadge-badge": {
                        width: { xs: "8px", sm: "12px" },
                        height: { xs: "8px", sm: "12px" },
                        borderRadius: "50%",
                        backgroundColor: "#EF4444",
                        border: `2px solid ${isDarkMode ? "var(--neutral-900)" : "#fff"}`,
                        boxShadow: badgeVisible
                          ? "0 0 0 2px rgba(239, 68, 68, 0.3), 0 2px 8px rgba(239, 68, 68, 0.4)"
                          : "none",
                        animation: badgeVisible ? "pulse 2s infinite" : "none",
                        top: { xs: "6px", sm: "8px" },
                        right: { xs: "6px", sm: "8px" },
                      },
                      "@keyframes pulse": {
                        "0%": {
                          boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.7)",
                        },
                        "70%": {
                          boxShadow: "0 0 0 6px rgba(239, 68, 68, 0)",
                        },
                        "100%": {
                          boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)",
                        },
                      },
                    }}
                  >
                    <NotificationsIcon
                      sx={{
                        color: badgeVisible
                          ? isDarkMode
                            ? "#EF4444"
                            : "#DC2626"
                          : isDarkMode
                            ? "var(--neutralWhite)"
                            : "#111827",
                        fontSize: { xs: 20, sm: 24 },
                        transition: "all 0.3s ease",
                        filter: badgeVisible
                          ? "drop-shadow(0 2px 4px rgba(239, 68, 68, 0.3))"
                          : "none",
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* popover for notification */}
              <Popover
                id={"notifications-popover"}
                open={openPopover}
                anchorEl={anchorPopover}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    maxWidth: isMobile ? "auto" : "400px",
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: isDarkMode
                      ? "0 12px 32px rgba(0, 0, 0, 0.45)"
                      : "0 10px 28px rgba(15, 23, 42, 0.14)",
                    padding: 2,
                    bgcolor: isDarkMode ? AIRBANK_DARK_CANVAS : "#fff",
                    border: `1px solid ${
                      isDarkMode
                        ? "rgba(249, 168, 37, 0.2)"
                        : "rgba(230, 145, 0, 0.14)"
                    }`,
                    maxHeight: "90vh",
                    /* Scrollbar styling */
                    "&::-webkit-scrollbar": {
                      width: 5,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: isDarkMode
                        ? "var(--neutral-800)"
                        : "#f0f0f0",
                      borderRadius: 8,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: isDarkMode
                        ? "rgba(249, 168, 37, 0.45)"
                        : "#c0c0c0",
                      borderRadius: 8,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(249, 168, 37, 0.65)"
                        : "#a0a0a0",
                    },
                  },
                }}
              >
                {isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography>{t("notifications")}</Typography>
                    <Tooltip title="Close" arrow>
                      <IconButton
                        onClick={handlePopoverClose}
                        size="small"
                        sx={{
                          pr: 0,
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {(() => {
                  const fcmNotifications = [
                    coffeeCallNotificationfromLayout && {
                      type: "coffee",
                      data: coffeeCallNotificationfromLayout,
                    },
                    regularCallNotificationfromLayout && {
                      type: "regularSession",
                      data: regularCallNotificationfromLayout,
                    },
                    groupCallInvitationfromLayout && {
                      type: "groupCallInvitation",
                      data: groupCallInvitationfromLayout,
                    },
                    groupCallNotificationfromLayout && {
                      type: "groupSession",
                      data: groupCallNotificationfromLayout,
                    },
                    // callConfirmationfromLayout && {
                    //   type: "callConfirmation",
                    //   data: callConfirmationfromLayout,
                    // },
                    // callRemainderfromLayout && {
                    //   type: "callRemainder",
                    //   data: callRemainderfromLayout,
                    // },
                    // groupCallConfirmationfromLayout && {
                    //   type: "missedGroupSession",
                    //   data: groupCallConfirmationfromLayout,
                    // },
                  ].filter(Boolean);

                  const allNotifications = [
                    ...fcmNotifications,
                    ...notificationList.map(
                      (notification: NotificationFullData) => ({
                        data: notification,
                        isSocket: true,
                      })
                    ),
                    // ...socketNotification.map((notification: any) => ({
                    //   data: notification,
                    //   isSocket: true,
                    // })),
                  ];

                  return allNotifications.length > 0 ? (
                    <>
                      {allNotifications.map((item, index) => (
                        <React.Fragment key={index}>
                          {item.isSocket ? (
                            <SocketNotification
                              closePopover={handlePopoverClose}
                              notification={item.data}
                            />
                          ) : (
                            <FCMNotifications
                              closePopover={handlePopoverClose}
                              notification={item}
                              notificationType={item.type}
                            />
                          )}
                          {index < allNotifications.length - 1 && (
                            <Divider sx={{ my: "10px" }} />
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        p: 2,
                        color: isDarkMode ? "var(--neutral-300)" : "#6B7280",
                        font: "var(--body-xs)",
                      }}
                    >
                      {t("no-new-notifications")}
                    </Typography>
                  );
                })()}

                <Divider sx={{ my: "10px" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                  }}
                >
                  <Link
                    to="/notification"
                    style={{
                      font: "var(--semibold-xs)",
                      color: isDarkMode ? "#fbbf24" : airbankOrangeDeep,
                      textDecoration: "none",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = airbankOrange;
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = isDarkMode
                        ? "#fbbf24"
                        : airbankOrangeDeep;
                      e.currentTarget.style.textDecoration = "none";
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.color = "#ffbe2e";
                    }}
                    onClick={handlePopoverClose}
                  >
                    {t("view_all_notifications")}
                  </Link>
                </Box>
              </Popover>
            </>

            {/* Language Selector */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                mr: 1,
              }}
            >
              <Box
                sx={{
                  "& .MuiButton-root": {
                    minWidth: "auto",
                    width: "auto",
                    height: "36px",
                    px: 1.5,
                    fontSize: 12,
                    borderRadius: 2,
                    border: isDarkMode
                      ? "1px solid rgba(249, 168, 37, 0.35)"
                      : "1px solid rgba(249, 168, 37, 0.28)",
                    bgcolor: isDarkMode
                      ? "rgba(249, 168, 37, 0.08)"
                      : "rgba(249, 168, 37, 0.05)",
                    color: isDarkMode ? "#fde68a" : airbankOrangeDeep,
                    fontWeight: 600,
                    textTransform: "none",
                    fontFamily: '"Lexend", sans-serif',
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(249, 168, 37, 0.14)"
                        : "rgba(249, 168, 37, 0.1)",
                      borderColor: isDarkMode
                        ? "rgba(249, 168, 37, 0.5)"
                        : "rgba(249, 168, 37, 0.4)",
                    },
                  },
                }}
              >
                <LanguageSelector variant="button" />
              </Box>
            </Box>

            {/* Language Selector - Mobile */}
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                alignItems: "center",
                mr: 0,
              }}
            >
              <Box
                sx={{
                  "& .MuiButton-root": {
                    minWidth: "auto",
                    width: "auto",
                    height: "22px",
                    px: 0.5,
                    py: 0,
                    fontSize: 9,
                    borderRadius: 1,
                    border: isDarkMode
                      ? "0.5px solid rgba(249, 168, 37, 0.4)"
                      : "0.5px solid rgba(249, 168, 37, 0.3)",
                    bgcolor: isDarkMode
                      ? "rgba(249, 168, 37, 0.08)"
                      : "rgba(249, 168, 37, 0.05)",
                    color: isDarkMode ? "#fde68a" : airbankOrangeDeep,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontFamily: '"Lexend", sans-serif',
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    lineHeight: 1,
                    letterSpacing: "0.5px",
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(249, 168, 37, 0.14)"
                        : "rgba(249, 168, 37, 0.1)",
                      borderColor: isDarkMode
                        ? "rgba(249, 168, 37, 0.55)"
                        : "rgba(249, 168, 37, 0.45)",
                      transform: "scale(1.05)",
                    },
                  },
                }}
              >
                <LanguageSelector variant="button" compact={true} />
              </Box>
            </Box>

            {/* Avatar */}
            <Tooltip title="Account menu" arrow>
              <IconButton
                onClick={handleAvatarClick}
                sx={{
                  padding: { xs: 0.5, sm: 1 },
                }}
              >
                <Avatar
                  alt="User"
                  src={
                    currentGTP?.profile_image ??
                    currentProfile?.profile_image ??
                    userInfo?.profile_image ??
                    ""
                  }
                  sx={{
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    bgcolor: "#FBBF24",
                    fontWeight: 600,
                    fontFamily: '"Lexend", sans-serif',
                    cursor: "pointer",
                    fontSize: { xs: 14, sm: 16 },
                    color: AIRBANK_DARK_CANVAS,
                    border: `2px solid ${
                      isDarkMode
                        ? "rgba(249, 168, 37, 0.55)"
                        : "rgba(230, 145, 0, 0.45)"
                    }`,
                  }}
                >
                  U
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Avatar Dropdown */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            boxShadow: isDarkMode
              ? "0 12px 32px rgba(0, 0, 0, 0.45)"
              : "0 10px 28px rgba(15, 23, 42, 0.14)",
            border: `1px solid ${
              isDarkMode
                ? "rgba(249, 168, 37, 0.2)"
                : "rgba(230, 145, 0, 0.14)"
            }`,
          },
        }}
      >
        <TalentandUserToggleMenu talentListClose={() => setAnchorEl(null)} />
      </Popover>
    </>
  );
};

export default Header;
